import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatDateLocal } from '@/lib/timeFormatUtils';

export interface EstablishmentClosure {
  id: string;
  establishment_id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  customer_message: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface UseEstablishmentClosuresOptions {
  establishmentId?: string;
}

export const useEstablishmentClosures = (options: UseEstablishmentClosuresOptions = {}) => {
  const { establishmentId } = options;
  const { toast } = useToast();
  const [closures, setClosures] = useState<EstablishmentClosure[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchClosures = useCallback(async () => {
    if (!establishmentId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('establishment_closures')
        .select('*')
        .eq('establishment_id', establishmentId)
        .order('start_date', { ascending: true });

      if (error) throw error;
      
      // Cast the data to our interface since types.ts is read-only
      setClosures((data || []) as unknown as EstablishmentClosure[]);
    } catch (error) {
      console.error('Erreur chargement fermetures:', error);
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  const addClosure = async (startDate: string, endDate: string, reason?: string, customerMessage?: string) => {
    if (!establishmentId) return;

    try {
      const { data, error } = await supabase
        .from('establishment_closures')
        .insert({
          establishment_id: establishmentId,
          start_date: startDate,
          end_date: endDate,
          reason: reason || null,
          customer_message: customerMessage || null,
        } as any)
        .select()
        .single();

      if (error) throw error;

      setClosures(prev => [...prev, data as unknown as EstablishmentClosure].sort(
        (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      ));
      
      toast({
        title: "Période de fermeture ajoutée",
        description: `Fermeture du ${startDate} au ${endDate}`,
      });

      return data;
    } catch (error) {
      console.error('Erreur ajout fermeture:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'ajouter la période de fermeture",
      });
      throw error;
    }
  };

  const updateClosure = async (id: string, startDate: string, endDate: string, reason?: string, customerMessage?: string) => {
    try {
      const { data, error } = await supabase
        .from('establishment_closures')
        .update({
          start_date: startDate,
          end_date: endDate,
          reason: reason || null,
          customer_message: customerMessage || null,
        } as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setClosures(prev => 
        prev.map(c => c.id === id ? (data as unknown as EstablishmentClosure) : c)
          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
      );
      
      toast({
        title: "Période de fermeture modifiée",
      });

      return data;
    } catch (error) {
      console.error('Erreur modification fermeture:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de modifier la période de fermeture",
      });
      throw error;
    }
  };

  const deleteClosure = async (id: string) => {
    try {
      const { error } = await supabase
        .from('establishment_closures')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setClosures(prev => prev.filter(c => c.id !== id));
      
      toast({
        title: "Période de fermeture supprimée",
      });
    } catch (error) {
      console.error('Erreur suppression fermeture:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la période de fermeture",
      });
      throw error;
    }
  };

  // Check if a date is within a closure period
  const isDateInClosure = useCallback((date: Date): { inClosure: boolean; reason?: string; customerMessage?: string } => {
    // Use formatDateLocal to avoid UTC timezone offset
    const dateStr = formatDateLocal(date);
    
    for (const closure of closures) {
      if (dateStr >= closure.start_date && dateStr <= closure.end_date) {
        return { 
          inClosure: true, 
          reason: closure.reason || undefined,
          customerMessage: closure.customer_message || undefined,
        };
      }
    }
    
    return { inClosure: false };
  }, [closures]);

  // Get active/upcoming closures (end_date >= today)
  const getActiveClosures = useCallback(() => {
    const today = formatDateLocal(new Date());
    return closures.filter(c => c.end_date >= today);
  }, [closures]);

  // Get past closures (end_date < today)
  const getPastClosures = useCallback(() => {
    const today = formatDateLocal(new Date());
    return closures.filter(c => c.end_date < today);
  }, [closures]);

  useEffect(() => {
    fetchClosures();

    if (!establishmentId) return;

    // Real-time subscription
    const subscription = supabase
      .channel(`establishment_closures_${establishmentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'establishment_closures',
          filter: `establishment_id=eq.${establishmentId}`,
        },
        () => {
          fetchClosures();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [establishmentId, fetchClosures]);

  return {
    closures,
    loading,
    addClosure,
    updateClosure,
    deleteClosure,
    isDateInClosure,
    getActiveClosures,
    getPastClosures,
    refetch: fetchClosures,
  };
};
